import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LightShowTrack } from '../_models/LightShowTrack';
import { Observable, from, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { BaseService } from '../_shared/base.service';
import * as moment from 'moment';
import { S3Service } from './s3.service';
import { Genre } from '../_models/Genre';
import { getUrl } from '../_shared/utils';
import { JWTTokenService } from './jwtToken.service';

@Injectable()
export class LightShowTrackService extends BaseService {
  userToken: any;
  decodedToken: any;

  genresUpdated = new EventEmitter();

  trackGenresUpdated = new EventEmitter();

  maxRetries = 6;
  timeBetweenRetries = 10000;

  constructor(private http: HttpClient, private s3Service: S3Service, jwtTokenService: JWTTokenService) {
    super(http, jwtTokenService);
  }

  saveTrackInfo(model: any) {
    this.RemoveFromCache('getLightShowTracks');
    return this.http.post(getUrl('track/saveTrackInfo'), model, this.buildOptions());
  }

  changeTrackState(model: any) {
    this.RemoveFromCache('getLightShowTracks');
    return this.http.post(getUrl('track/changeTrackState'), model, this.buildOptions());
  }

  deleteTrack(id: number) {
    this.RemoveFromCache('getLightShowTracks');
    return this.http.delete(getUrl(`assets/track/delete/${id}`), this.buildOptions());
  }


  getUserTracks() {
    return this.CachedCall('getLightShowTracks', this.http.get<LightShowTrack[]>(getUrl('assets/track'), this.buildOptions()));
  }

  getTrack(id: number) {
    this.RemoveFromCache('getLightShowTrack' + id);
    return this.CachedCall('getLightShowTrack' + id, this.http.get<any>(getUrl('assets/track/' + id), this.buildOptions()));
  }

  toggleTrackVisibility(trackId: number): Observable<boolean> {
    this.RemoveFromCache('getLightShowTrack' + trackId);
    return this.http.get<boolean>(getUrl('assets/track/toggle-visibility/' + trackId), this.buildOptions());
  }

  uploadTrack(trackFile: File, startActive: boolean, uploadToS3: boolean = true): Observable<LightShowTrack> {
    if (trackFile == null) { return; }

    this.RemoveFromCache('getLightShowTracks');

    return this.getDurationString(trackFile).pipe(
      mergeMap(durationString => {
        const body: any = {
          StartActive: startActive,
          TrackName: trackFile.name,
          TrackDuration: durationString
        };

        return this.http.post<LightShowTrack>(getUrl('assets/track/add'), body, this.buildOptions());
    }), 
      mergeMap(track => {
        if (uploadToS3){
          return this.s3Service.uploadFile(trackFile, track.folderName.toLowerCase().replace(/ /g, '-'), "get-signed-s3-url").pipe(map(s3Res => track));
        }

        return of(track);
    }));
  }

  copyLightShowToLibTrack(trackData: any): Observable<any> {
    this.RemoveFromCache('getLightShowTrack' + trackData.trackId);
    return this.http.post(getUrl('assets/track/copy-lightshow'), trackData, this.buildOptions());
  }

  public updateTrack(trackData: any, saveLibTrackChanges: boolean): Observable<any> {
    trackData.saveLibTrackChanges = saveLibTrackChanges;
    this.RemoveFromCache('getLightShowTrack' + trackData.trackId);
    return this.http.post(getUrl('assets/track/update-portal'), trackData, this.buildOptions());
  }

  getLightShowTracks() {
    this.RemoveFromCache('getLightShowTracks');
    return this.CachedCall('getLightShowTracks', this.http.get<LightShowTrack[]>(getUrl('assets/track'), this.buildOptions()));
  }

  getAllGenres() {
    return this.CachedCall('getTracksGenres', this.http.get<Genre[]>(getUrl('assets/genres'), this.buildOptions()));
  }

  deleteGenre(genreId: number) {
    this.RemoveFromCache('getTracksGenres');
    return this.http.delete(getUrl(`assets/genres/${genreId}`), this.buildOptions());
  }

  addGenre(genre: Genre) {
    this.RemoveFromCache('getTracksGenres');
    return this.http.post(getUrl('assets/genres'), genre, this.buildOptions());
  }

  checkForReencodedFile(url: string): Observable<boolean>{
    let retries = 0;
    const headers = new HttpHeaders({'Content-Type': 'audio'});
    const fileObservable = from(new Promise<boolean>(async(resolve, reject) => {
        while(retries < this.maxRetries){
          try{
            await new Promise<void>((resolve, reject) => {
              setTimeout(() => {
                resolve();
              }, this.timeBetweenRetries);
            });
            await this.http.head(url, {headers: headers}).toPromise()
            resolve(true);
          } catch (error){
            retries++;
            console.log(`Retries: ${retries}`);
          }
        }
        reject(false);
    }));
    return fileObservable;
  }

  public getDurationSeconds(track: File): Observable<number> {
    return new Observable(subscriber => {
      const objectUrl = URL.createObjectURL(track);
      const audio = new Audio(objectUrl);
      audio.addEventListener('loadeddata', function () {
        const duration = moment.duration(audio.duration, 'seconds');
        subscriber.next(duration.asSeconds());
        subscriber.complete();
        URL.revokeObjectURL(objectUrl);
      });
    });
  }

  public getDurationString(track: File): Observable<string> {
    return new Observable(subscriber => {
      const objectUrl = URL.createObjectURL(track);
      const audio = new Audio(objectUrl);
      audio.addEventListener('loadeddata', function () {
        const duration = moment.duration(audio.duration, 'seconds');
        const hours = duration.hours() < 10 ? '0' + duration.hours().toString() : duration.hours().toString();
        const minutes = duration.minutes() < 10 ? '0' + duration.minutes().toString() : duration.minutes().toString();
        const seconds = duration.seconds() < 10 ? '0' + duration.seconds().toString() : duration.seconds().toString();
        const durationString = hours + ':' + minutes + ':' + seconds;
        subscriber.next(durationString);
        subscriber.complete();
        URL.revokeObjectURL(objectUrl);
      });
    });
  }

}

